import { useEffect, useState } from "react";
import StatCard from "../components/StatCard";
import CustomPieChart from "../components/CustomPieChart";
import SplashScreen from "../components/SplashScreen";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import MapDash from "../components/MapDash";
import { useTranslation } from "react-i18next";

const defaultData = {
  eBuses: "0",
  cngStations: "0",
  microbusesGasPercentage: "0",
  electricVehicles: "0",
  chargingPoints: "0",
  bikeAndWalkTracksLength: "0",
  bikeTripsCount: "0",
  bikeSharingStations: "0",
  gasTaxisPercentage: "0",
  touristVehiclesCars: "0",
  touristVehiclesMinibuses: "0",
  touristVehiclesBuses: "0",
};

const Transport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(defaultData);
  const [showSplash, setShowSplash] = useState(true);
  const [filter, setFilter] = useState("all");
  const [mapData, setMapData] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("transportData")) || [];
    setMapData(saved);
    if (saved.length > 0) {
      const totals = saved.reduce((acc, item) => {
        for (let key in item) {
          if (key === "latitude" || key === "longitude") continue;
          const value = Number(item[key]);
          acc[key] = (acc[key] || 0) + (isNaN(value) ? 0 : value);
        }
        return acc;
      }, {});
      setData(totals);
    } else {
      setData(defaultData);
    }

    setTimeout(() => setShowSplash(false), 200);
  }, []);

  if (showSplash) return <SplashScreen />;

  const touristVehicleData = [
    { name: t("tourist_vehicles_cars"), value: Number(data.touristVehiclesCars) },
    { name: t("tourist_vehicles_minibuses"), value: Number(data.touristVehiclesMinibuses) },
    { name: t("tourist_vehicles_buses"), value: Number(data.touristVehiclesBuses) },
  ];

  const filteredData = () => {
    if (filter === "electric") {
      return {
        eBuses: data.eBuses,
        electricVehicles: data.electricVehicles,
        chargingPoints: data.chargingPoints,
      };
    }
    if (filter === "cars") {
      return {
        touristVehiclesCars: data.touristVehiclesCars,
        touristVehiclesMinibuses: data.touristVehiclesMinibuses,
        touristVehiclesBuses: data.touristVehiclesBuses,
      };
    }
    return data;
  };

  const filtered = filteredData();

  return (
    <>
      <Helmet>
        <title>{t("transport")} | {t("dashboard")}</title>
      </Helmet>
      <div className="flex flex-col gap-4 md:gap-3 text-start w-full h-auto md:h-full md:min-h-0 md:overflow-hidden">
        <div className="flex flex-col gap-2 text-start">
          <h1 className="mx-auto text-xl sm:text-2xl md:text-3xl font-extrabold p-2.5 bg-white/55 rounded-md backdrop-blur-md w-full flex justify-center">
            {t("transport")}
          </h1>
          <div className="flex flex-col sm:flex-row gap-2 w-full items-center justify-between">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border p-2 rounded w-full sm:w-1/2 md:w-1/3 lg:w-1/4 dark:bg-gray-600 dark:text-white bg-white text-xs sm:text-sm"
            >
              <option value="all">{t("all")}</option>
              <option value="electric">{t("transport_electric")}</option>
              <option value="cars">{t("tourist_vehicles_cars")}</option>
            </select>
            <button
              onClick={() => navigate("/TransportForm")}
              className="bg-green-500 hover:bg-green-600 transition-colors text-white p-2 rounded w-full sm:w-auto text-xs sm:text-sm font-semibold mt-2 sm:mt-0"
            >
              {t("edit_data")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 min-h-0">
          {/* العمود الأول - الكروت */}
          <div
            className="col-span-1 overflow-y-auto pr-2 h-[60vh] md:h-auto md:flex-1 md:min-h-0 flex flex-col gap-4"
            dir="ltr"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-1 lg:grid-cols-3 gap-3">
              <StatCard
                title={t("electric_buses")}
                value={filtered.eBuses || "-"}
              />
              <StatCard
                title={t("cng_stations")}
                value={filtered.cngStations || "-"}
              />
              <StatCard
                title={t("microbuses_cng")}
                value={
                  filtered.microbusesGasPercentage
                    ? `${filtered.microbusesGasPercentage}%`
                    : "-"
                }
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-1 lg:grid-cols-3 gap-3">
              <StatCard
                title={t("electric_vehicles")}
                value={filtered.electricVehicles || "-"}
              />
              <StatCard
                title={t("ev_charging_points")}
                value={filtered.chargingPoints || "-"}
              />
              <StatCard
                title={t("bike_walk_tracks")}
                value={filtered.bikeAndWalkTracksLength || "-"}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-1 lg:grid-cols-3 gap-3">
              <StatCard
                title={t("daily_bike_trips")}
                value={filtered.bikeTripsCount || "-"}
              />
              <StatCard
                title={t("bike_sharing_stations")}
                value={filtered.bikeSharingStations || "-"}
              />
              <StatCard
                title={t("taxis_cng")}
                value={
                  filtered.gasTaxisPercentage
                    ? `${filtered.gasTaxisPercentage}%`
                    : "-"
                }
              />
            </div>

            <div className="grid">
              <CustomPieChart
                data={touristVehicleData}
                dataKey="value"
                nameKey="name"
                colors={["#60a5fa", "#34d399", "#f59e0b"]}
                title={t("tourists_by_transport")}
              />
            </div>
          </div>

          {/* العمود الثاني - الخريطة */}
          <div className="md:col-span-2 h-[60vh] md:h-auto md:flex-1 md:min-h-0 rounded-xl leaflet-container !bg-transparent min-h-[300px] shadow-md border border-white/20">
            <MapDash initialData={mapData} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Transport;


